import { graphqlClient } from '@/services/core/graphqlClient';
import { GRAPHQL_RESOURCES } from '@/services/resources';
import { PATIENT_QUERIES } from '@/services/resources/patientsGquery';
import { ClinicNumber, ClinicNumberSourceTypesResponse } from '@/types/clinicTypes';

// Type definitions for clinics
export interface Clinic {
  id: number;
  name: string;
  address?: string;
  phone?: string;
  isActive: boolean;
}

export interface Facility {
  facility_id: number;
  facility_name: string;
  clinic_id?: number;
}

export interface Provider {
  provider_id: number;
  provider_name: string;
  specialty?: string;
  clinic_id?: number;
  facility_id?: number;
}

/**
 * ClinicsGraphqlService - Handles all clinic and facility related GraphQL operations
 * Consumes organized queries from clinicsGquery.ts
 */
export class ClinicsGraphqlService {
  /**
   * Get facilities by clinic ID from emr_clinic_facility
   */
  async getFacilitiesByClinic(clinicId: number, hasuraToken: string): Promise<Array<{
    facility_id: number;
    facility_name: string;
  }>> {
    const query = `
      query GetFacilitiesByClinic($clinicId: Int!) {
        emr_clinic_facility(where: {clinic_id: {_eq: $clinicId}}) {
          clinic_facility_id
          facility_name
        }
      }
    `;
    
    const result = await graphqlClient.query<{
      emr_clinic_facility: Array<{
        clinic_facility_id: number;
        facility_name: string;
      }>
    }>(
      query,
      { clinicId },
      hasuraToken
    );
    
    const facilities = result.data?.emr_clinic_facility || [];
    return facilities.map(f => ({
      facility_id: f.clinic_facility_id,
      facility_name: f.facility_name
    }));
  }

  /**
   * Get available clinics
   */
  async getAvailableClinics(hasuraToken: string): Promise<Array<{
    clinic_id: number;
    clinic_name: string;
    patient_clinic_timezone: string;
  }>> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_AVAILABLE_CLINICS,
      {},
      hasuraToken
    );
    return result.data?.pep_vw_available_clinics || [];
  }

  /**
   * Get providers by clinic
   */
  async getProvidersByClinic(clinicId: number, hasuraToken: string): Promise<Array<{
    provider_id: number;
    provider_name: string;
  }>> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_PROVIDERS_BY_CLINIC,
      { clinicId },
      hasuraToken
    );
    return result.data?.pep_vw_consolidated_data || [];
  }

  /**
   * Get providers from emr_clinic_staff by clinic ID
   */
  async getProvidersByClinicFromEMR(clinicId: number, hasuraToken: string): Promise<Array<{
    staff_id: number;
    staff_name: string;
  }>> {
    const query = `
      query GetProvidersFromEMR($clinicId: Int!) {
        emr_clinic_staff(where: {emr_clinic_staff_provider: {clinic_id: {_eq: $clinicId}}}) {
          staff_id
          first_name
          is_active
          last_name
          middle_name
          npi_number
        }
      }
    `;
    
    const result = await graphqlClient.query<{
      emr_clinic_staff: Array<{
        staff_id: number;
        first_name: string;
        is_active: boolean;
        last_name: string;
        middle_name: string | null;
        npi_number: string | null;
      }>
    }>(
      query,
      { clinicId },
      hasuraToken
    );
    
    // Map to staff_id and staff_name format
    const providers = result.data?.emr_clinic_staff || [];
    return providers
      .filter(p => p.is_active)
      .map(p => ({
        staff_id: p.staff_id,
        staff_name: [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(' ')
      }));
  }

  /**
   * Get providers by clinic and facilities
   */
  async getProvidersByClinicAndFacilities(clinicId: number, facilityIds: number[], hasuraToken: string): Promise<Provider[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_PROVIDERS_BY_CLINIC_AND_FACILITIES,
      { clinicId, facilityIds },
      hasuraToken
    );
    return result.data?.pep_vw_consolidated_data || [];
  }

  /**
   * Get providers by facilities
   */
  async getProvidersByFacilities(clinicId: number, facilityIds: number[], hasuraToken: string): Promise<Provider[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_PROVIDERS_BY_FACILITIES,
      { clinicId, facilityIds },
      hasuraToken
    );
    return result.data?.pep_vw_consolidated_data || [];
  }

  /**
   * Get available clinics with enroller
   */
  async getAvailableClinicsWithEnroller(hasuraToken: string): Promise<any[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_AVAILABLE_CLINICS_WITH_ENROLLER,
      {},
      hasuraToken
    );
    return result.data?.pep_vw_available_clinics || [];
  }

  /**
   * Get clinic appointments
   */
  async getClinicAppointments(limit: number, offset: number, whereClause: string): Promise<any[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_CLINIC_APPOINTMENTS,
      { limit, offset, whereClause }
    );
    return result.data?.pep_vw2_clinic_appointments || [];
  }

  /**
   * Get facilities by clinic (simple version)
   */
  async getFacilitiesByClinicSimple(clinicId: number): Promise<Facility[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_FACILITIES_BY_CLINIC_SIMPLE,
      { clinicId }
    );
    return result.data?.pep_vw_consolidated_data || [];
  }

  /**
   * Get providers by clinic (simple version)
   */
  async getProvidersByClinicSimple(clinicId: number): Promise<Provider[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_PROVIDERS_BY_CLINIC_SIMPLE,
      { clinicId }
    );
    return result.data?.pep_vw_consolidated_data || [];
  }

  /**
   * Get available facilities by clinic
   */
  async getAvailableFacilitiesByClinic(clinicIds: number[]): Promise<Facility[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.AVAILABLE_FACILITIES_BY_CLINIC,
      { clinicIds }
    );
    return result.data?.pep_vw_consolidated_data || [];
  }

  /**
   * Get available providers by clinic
   */
  async getAvailableProvidersByClinic(clinicIds: number[]): Promise<Provider[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.AVAILABLE_PROVIDERS_BY_CLINIC,
      { clinicIds }
    );
    return result.data?.pep_vw_consolidated_data || [];
  }

  /**
   * Get available providers by clinic and facilities
   */
  async getAvailableProvidersByClinicAndFacilities(clinicIds: number[], facilityIds: number[]): Promise<Provider[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.AVAILABLE_PROVIDERS_BY_CLINIC_AND_FACILITIES,
      { clinicIds, facilityIds }
    );
    return result.data?.pep_vw_consolidated_data || [];
  }

  /**
   * Get outbound numbers for call functionality (REST API)
   */
  async getOutboundNumbers(
    clinicId: number, 
    numberType: 'call' | 'sms', 
    token: string, 
    originType: string = 'Enrollment'
  ): Promise<import('@/types/clinicTypes').OutboundNumberResponse> {
    try {
      const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://sandbox.hbox.ai';
      const url = `${API_BASE}/twilio/outbound-number?clinicId=${clinicId}&numberType=${numberType}&originType=${originType}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'authorization': `Bearer ${token}`,
          'content-type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch outbound numbers: ${response.status}`);
      }

      const data = await response.json();
      return data || { outboundNumber: [] };
    } catch (error) {
      console.error('Error fetching outbound numbers:', error);
      return { outboundNumber: [] };
    }
  }

  /**
   * Get clinic numbers for call functionality (legacy GraphQL - deprecated)
   */
  async getClinicNumbers(clinicId: number, stateId: number, hasuraToken: string): Promise<ClinicNumber[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.CLINIC_NUMBERS,
      { clinicId, stateId },
      hasuraToken
    );
    return result.data?.api_origin_number_master || [];
  }

  /**
   * Get clinic number source types for call functionality (legacy GraphQL - deprecated)
   */
  async getClinicNumberSourceTypes(hasuraToken: string): Promise<Array<{
    is_deleted: boolean;
    number_source: string;
    number_source_id: number;
  }>> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.CLINIC_NUMBER_SOURCE_TYPES,
      {},
      hasuraToken
    );
    return result.data?.reference_number_source || [];
  }

  /**
   * Get clinic info by clinic ID for SMS campaigns
   */
  async getClinicInfo(clinicId: number, hasuraToken: string): Promise<Array<{
    id: number;
    state_id: number;
    name: string;
  }>> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_CLINIC_INFO,
      { hccId: clinicId },
      hasuraToken
    );
    return result.data?.api_healthcarecenter || [];
  }

  /**
   * Get distinct timezones
   */
  async getTimezones(hasuraToken: string): Promise<Array<{
    timezone: string;
  }>> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_TIMEZONES,
      {},
      hasuraToken
    );
    return result.data?.api_healthcarecenter || [];
  }

  /**
   * Get active healthcare centers
   */
  async getActiveHealthcareCenters(hasuraToken: string): Promise<Array<{
    id: number;
    active: boolean;
    description: string;
    name: string;
  }>> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_ACTIVE_HEALTHCARE_CENTERS,
      {},
      hasuraToken
    );
    return result.data?.api_healthcarecenter || [];
  }

  /**
   * Get primary diagnoses by clinic ID
   */
  async getPrimaryDiagnosisByClinic(
    clinicId: number, 
    hasuraToken: string,
    facilityIds?: number[],
    providerIds?: number[]
  ): Promise<string[]> {
    const variables: any = { clinicID: clinicId };
    
    // Build where clause dynamically
    let whereClause = `ccm_status: {_nin: [1,2,3,7,8,9,10]}, clinic_id: {_eq: $clinicID}`;
    const variableDefinitions = ['$clinicID: Int!'];
    
    if (facilityIds && facilityIds.length > 0) {
      variables.facilityIDs = facilityIds;
      whereClause += `, facility_id: {_in: $facilityIDs}`;
      variableDefinitions.push('$facilityIDs: [Int!]');
    }
    
    if (providerIds && providerIds.length > 0) {
      variables.providerIDs = providerIds;
      whereClause += `, provider_id: {_in: $providerIDs}`;
      variableDefinitions.push('$providerIDs: [Int!]');
    }
    
    // Build dynamic query
    const query = `
      query GetPrimaryDiagnosisByClinic(${variableDefinitions.join(', ')}) {
        pep_vw_consolidated_data(
          where: {
            ${whereClause}
          }, 
          distinct_on: primary_dx
        ) {
          primary_dx
        }
      }
    `;
    
    const result = await graphqlClient.query<{ 
      pep_vw_consolidated_data: Array<{ primary_dx: string }> 
    }>(
      query,
      variables,
      hasuraToken
    );
    const diagnoses = result.data?.pep_vw_consolidated_data || [];
    return diagnoses
      .map(item => item.primary_dx)
      .filter(dx => dx && dx.trim() !== '');
  }

  /**
   * Get secondary diagnoses by clinic ID
   */
  async getSecondaryDiagnosisByClinic(
    clinicId: number, 
    hasuraToken: string,
    facilityIds?: number[],
    providerIds?: number[]
  ): Promise<string[]> {
    const variables: any = { clinicID: clinicId };
    
    // Build where clause dynamically
    let whereClause = `ccm_status: {_nin: [1,2,3,7,8,9,10]}, clinic_id: {_eq: $clinicID}`;
    const variableDefinitions = ['$clinicID: Int!'];
    
    if (facilityIds && facilityIds.length > 0) {
      variables.facilityIDs = facilityIds;
      whereClause += `, facility_id: {_in: $facilityIDs}`;
      variableDefinitions.push('$facilityIDs: [Int!]');
    }
    
    if (providerIds && providerIds.length > 0) {
      variables.providerIDs = providerIds;
      whereClause += `, provider_id: {_in: $providerIDs}`;
      variableDefinitions.push('$providerIDs: [Int!]');
    }
    
    // Build dynamic query
    const query = `
      query GetSecondaryDiagnosisByClinic(${variableDefinitions.join(', ')}) {
        pep_vw_consolidated_data(
          where: {
            ${whereClause}
          }, 
          distinct_on: secondary_dx
        ) {
          secondary_dx
        }
      }
    `;
    
    const result = await graphqlClient.query<{ 
      pep_vw_consolidated_data: Array<{ secondary_dx: string }> 
    }>(
      query,
      variables,
      hasuraToken
    );
    const diagnoses = result.data?.pep_vw_consolidated_data || [];
    return diagnoses
      .map(item => item.secondary_dx)
      .filter(dx => dx && dx.trim() !== '');
  }

  /**
   * Get primary insurance providers by clinic ID
   */
  async getPrimaryInsuranceByClinic(
    clinicId: number, 
    hasuraToken: string,
    facilityIds?: number[],
    providerIds?: number[]
  ): Promise<string[]> {
    const variables: any = { clinicID: clinicId };
    
    // Build where clause dynamically
    let whereClause = `ccm_status: {_nin: [1,2,3,7,8,9,10]}, clinic_id: {_eq: $clinicID}`;
    const variableDefinitions = ['$clinicID: Int!'];
    
    if (facilityIds && facilityIds.length > 0) {
      variables.facilityIDs = facilityIds;
      whereClause += `, facility_id: {_in: $facilityIDs}`;
      variableDefinitions.push('$facilityIDs: [Int!]');
    }
    
    if (providerIds && providerIds.length > 0) {
      variables.providerIDs = providerIds;
      whereClause += `, provider_id: {_in: $providerIDs}`;
      variableDefinitions.push('$providerIDs: [Int!]');
    }
    
    // Build dynamic query
    const query = `
      query GetPrimaryInsuranceByClinic(${variableDefinitions.join(', ')}) {
        pep_vw_consolidated_data(
          where: {
            ${whereClause}
          }, 
          distinct_on: primary_insurance_provider
        ) {
          primary_insurance_provider
        }
      }
    `;
    
    const result = await graphqlClient.query<{ 
      pep_vw_consolidated_data: Array<{ primary_insurance_provider: string }> 
    }>(
      query,
      variables,
      hasuraToken
    );
    const insuranceProviders = result.data?.pep_vw_consolidated_data || [];
    return insuranceProviders
      .map(item => item.primary_insurance_provider)
      .filter(insurance => insurance && insurance.trim() !== '');
  }

  /**
   * Get secondary insurance providers by clinic ID
   */
  async getSecondaryInsuranceByClinic(
    clinicId: number, 
    hasuraToken: string,
    facilityIds?: number[],
    providerIds?: number[]
  ): Promise<string[]> {
    const variables: any = { clinicID: clinicId };
    
    // Build where clause dynamically
    let whereClause = `ccm_status: {_nin: [1,2,3,7,8,9,10]}, clinic_id: {_eq: $clinicID}`;
    const variableDefinitions = ['$clinicID: Int!'];
    
    if (facilityIds && facilityIds.length > 0) {
      variables.facilityIDs = facilityIds;
      whereClause += `, facility_id: {_in: $facilityIDs}`;
      variableDefinitions.push('$facilityIDs: [Int!]');
    }
    
    if (providerIds && providerIds.length > 0) {
      variables.providerIDs = providerIds;
      whereClause += `, provider_id: {_in: $providerIDs}`;
      variableDefinitions.push('$providerIDs: [Int!]');
    }
    
    // Build dynamic query
    const query = `
      query GetSecondaryInsuranceByClinic(${variableDefinitions.join(', ')}) {
        pep_vw_consolidated_data(
          where: {
            ${whereClause}
          }, 
          distinct_on: secondary_insurance_provider
        ) {
          secondary_insurance_provider
        }
      }
    `;
    
    const result = await graphqlClient.query<{ 
      pep_vw_consolidated_data: Array<{ secondary_insurance_provider: string }> 
    }>(
      query,
      variables,
      hasuraToken
    );
    const insuranceProviders = result.data?.pep_vw_consolidated_data || [];
    return insuranceProviders
      .map(item => item.secondary_insurance_provider)
      .filter(insurance => insurance && insurance.trim() !== '');
  }

  /**
   * Get insurance types by clinic ID
   */
  async getInsuranceTypeByClinic(
    clinicId: number, 
    hasuraToken: string,
    facilityIds?: number[],
    providerIds?: number[]
  ): Promise<string[]> {
    const variables: any = { clinicID: clinicId };
    
    // Build where clause dynamically
    let whereClause = `ccm_status: {_nin: [1,2,3,7,8,9,10]}, clinic_id: {_eq: $clinicID}`;
    const variableDefinitions = ['$clinicID: Int!'];
    
    if (facilityIds && facilityIds.length > 0) {
      variables.facilityIDs = facilityIds;
      whereClause += `, facility_id: {_in: $facilityIDs}`;
      variableDefinitions.push('$facilityIDs: [Int!]');
    }
    
    if (providerIds && providerIds.length > 0) {
      variables.providerIDs = providerIds;
      whereClause += `, provider_id: {_in: $providerIDs}`;
      variableDefinitions.push('$providerIDs: [Int!]');
    }
    
    // Build dynamic query
    const query = `
      query GetInsuranceTypeByClinic(${variableDefinitions.join(', ')}) {
        pep_vw_consolidated_data(
          where: {
            ${whereClause}
          }, 
          distinct_on: insurance_type
        ) {
          insurance_type
        }
      }
    `;
    
    const result = await graphqlClient.query<{ 
      pep_vw_consolidated_data: Array<{ insurance_type: string }> 
    }>(
      query,
      variables,
      hasuraToken
    );
    const insuranceTypes = result.data?.pep_vw_consolidated_data || [];
    return insuranceTypes
      .map(item => item.insurance_type)
      .filter(type => type && type.trim() !== '');
  }

  /**
   * Get primary insurance list from emr_patient_insurance
   */
  async getPrimaryInsuranceList(hasuraToken: string): Promise<Array<{
    insurance_payor: string;
    insurance_id: number;
  }>> {
    const query = `
      query GetPrimaryInsuranceList {
        emr_patient_insurance(
          where: {coordination_of_benefits: {_eq: 1}}, 
          distinct_on: insurance_payor
        ) {
          insurance_payor
          insurance_id
        }
      }
    `;
    
    const result = await graphqlClient.query<{
      emr_patient_insurance: Array<{
        insurance_payor: string;
        insurance_id: number;
      }>
    }>(
      query,
      {},
      hasuraToken
    );
    return result.data?.emr_patient_insurance || [];
  }
  /*
   * Get all enrollment numbers for outbound dialer
   */
  async getAllEnrollmentNumbers(token: string): Promise<import('@/types/clinicTypes').OutboundNumberResponse> {
    try {
      const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://sandbox.hbox.ai';
      const url = `${API_BASE}/twilio/outbound-number?originType=Enrollment&numberType=call`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'authorization': `Bearer ${token}`,
          'content-type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Enrollment numbers: ${response.status}`);
      }

      const data = await response.json();
      return data || { outboundNumber: [] };
    } catch (error) {
      console.error('Error fetching Enrollment numbers:', error);
      return { outboundNumber: [] };
    }
  }

  /**
   * Get outbound call allowed roles from constants
   */
  async getOutboundCallRoles(hasuraToken: string): Promise<string[]> {
    const result = await graphqlClient.query(
      GRAPHQL_RESOURCES.GET_OUTBOUND_CALL_ROLES,
      {},
      hasuraToken
    );
    
    const apiConstant = result.data?.api_constant?.[0];
    if (apiConstant && apiConstant.field_value) {
      // Split comma-separated string into array and trim whitespace
      return apiConstant.field_value.split(',').map((role: string) => role.trim());
    }
    
    return [];
  }

  /**
   * Get secondary insurance list from emr_patient_insurance
   */
  async getSecondaryInsuranceList(hasuraToken: string): Promise<Array<{
    insurance_payor: string;
    insurance_id: number;
  }>> {
    const query = `
      query GetSecondaryInsuranceList {
        emr_patient_insurance(
          where: {coordination_of_benefits: {_eq: 2}}, 
          distinct_on: insurance_payor
        ) {
          insurance_payor
          insurance_id
        }
      }
    `;
    
    const result = await graphqlClient.query<{
      emr_patient_insurance: Array<{
        insurance_payor: string;
        insurance_id: number;
      }>
    }>(
      query,
      {},
      hasuraToken
    );
    
    return result.data?.emr_patient_insurance || [];
  }
}

// Export singleton instance
export const clinicsGraphqlService = new ClinicsGraphqlService();